import { useState, useEffect } from "react";
import axios from "axios";
import {
  Shield, Globe, AlertTriangle, TrendingUp, Users, Zap, Layers,
  ChevronDown, ChevronUp, Search, Target, Building
} from "lucide-react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell
} from "recharts";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DIM_LABELS = {
  impact_translation: "Impact Translation",
  enforcement_strength: "Enforcement",
  ethical_depth: "Ethical Depth",
  people_equity: "People & Equity",
  convergence_readiness: "Convergence",
  innovation_enablement: "Innovation",
  ecosystem_empowerment: "Ecosystem",
};

const DIM_ICONS = {
  impact_translation: Target,
  enforcement_strength: Shield,
  ethical_depth: Layers,
  people_equity: Users,
  convergence_readiness: Globe,
  innovation_enablement: Zap,
  ecosystem_empowerment: TrendingUp,
};

const GAP_COLORS = {
  policy_product: "#f59e0b", product_policy: "#3b82f6", enforcement: "#ef4444",
  dimension: "#a855f7", people: "#ec4899", convergence: "#6366f1", ecosystem: "#10b981",
};

const priColor = (s) => s >= 60 ? "#10b981" : s >= 40 ? "#f59e0b" : "#ef4444";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name || p.dataKey} style={{ color: p.color || "#94a3b8" }}>{p.name}: {typeof p.value === "number" ? p.value.toFixed(1) : p.value}</p>
      ))}
    </div>
  );
};

/* ── Skeleton shimmer blocks ── */
const Shimmer = ({ className }) => (
  <div className={`animate-pulse bg-white/[0.04] rounded-lg ${className}`} />
);

const HeroSkeleton = () => (
  <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #150a1a 50%, #0a0f1a 100%)" }}>
    <div className="relative px-8 py-10 text-center space-y-3">
      <Shimmer className="h-3 w-32 mx-auto" />
      <Shimmer className="h-9 w-80 mx-auto" />
      <Shimmer className="h-4 w-96 mx-auto" />
      <Shimmer className="h-12 w-48 mx-auto rounded-full mt-4" />
    </div>
  </div>
);

const ChartSkeleton = () => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
    <Shimmer className="h-3 w-32 mb-4" />
    <div className="h-56 flex items-end gap-4 px-4">
      {[70,50,45,42,40,35,30].map((h, i) => (
        <Shimmer key={i} className="flex-1" style={{ height: `${h}%` }} />
      ))}
    </div>
  </div>
);

const InsightSkeleton = () => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-3">
    <Shimmer className="h-3 w-24" />
    {[1,2,3,4].map(i => (
      <div key={i} className="flex items-start gap-3 px-3 py-2 bg-white/[0.01] rounded-lg">
        <Shimmer className="w-4 h-4 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Shimmer className="h-3 w-20" />
          <Shimmer className="h-2.5 w-full" />
        </div>
      </div>
    ))}
  </div>
);

const CorpSkeleton = () => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
    <div className="px-5 py-4 border-b border-white/[0.06]">
      <Shimmer className="h-4 w-48" />
      <Shimmer className="h-2.5 w-64 mt-2" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 p-4">
      {[1,2,3,4,5].map(i => (
        <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3 space-y-2">
          <Shimmer className="h-3 w-16" />
          <Shimmer className="h-6 w-12" />
          <Shimmer className="h-1.5 w-full" />
        </div>
      ))}
    </div>
  </div>
);

const RankingSkeleton = () => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
    <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-4">
      <div className="flex-1"><Shimmer className="h-4 w-48" /><Shimmer className="h-2.5 w-64 mt-2" /></div>
      <Shimmer className="h-7 w-44 rounded-lg" />
    </div>
    {[1,2,3,4,5,6].map(i => (
      <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.03]">
        <Shimmer className="w-6 h-4" />
        <div className="flex-1 space-y-1"><Shimmer className="h-3.5 w-32" /><Shimmer className="h-2.5 w-48" /></div>
        <Shimmer className="w-10 h-6" />
      </div>
    ))}
  </div>
);

/* ── Country Row (expanded detail) ── */
const CountryRow = ({ country, rank, expanded, onToggle }) => {
  const dims = country.dimensions || {};
  const radarData = Object.entries(dims).map(([k, v]) => ({
    dim: DIM_LABELS[k]?.split(" ")[0] || k, value: v, fullMark: 100,
  }));

  return (
    <div className="border-b border-white/[0.04] last:border-b-0" data-testid={`readiness-row-${rank}`}>
      <div onClick={onToggle} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors">
        <span className="text-xs font-bold text-slate-500 w-6 text-right">{rank}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-white">{country.country}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.04] text-slate-400">{country.region}</span>
          </div>
          <p className="text-[10px] text-slate-500">
            {country.policy_count} policies &middot; {country.product_count.toLocaleString()} AI products &middot; {country.care_product_ratio}% care
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <p className="text-lg font-black" style={{ color: priColor(country.pri_score) }}>{country.pri_score}</p>
            <p className="text-[9px] text-slate-500">PRI</p>
          </div>
          {country.gap_count > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">
              {country.gap_count} gaps
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-5 pt-2 ml-9 space-y-4 animate-fade-in">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Readiness Dimensions</h4>
              <div className="h-52">
                <ResponsiveContainer>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#1e293b" />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar dataKey="value" stroke="#a855f7" fill="#a855f7" fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Dimension Breakdown</h4>
              <div className="space-y-2">
                {Object.entries(dims).map(([k, v]) => {
                  const Icon = DIM_ICONS[k] || Shield;
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <Icon className="w-3 h-3 text-slate-500 flex-shrink-0" />
                      <span className="text-[10px] text-slate-400 w-28 flex-shrink-0">{DIM_LABELS[k]}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: v >= 60 ? "#10b981" : v >= 30 ? "#f59e0b" : "#ef4444" }} />
                      </div>
                      <span className="text-[10px] font-bold w-8 text-right" style={{ color: v >= 60 ? "#10b981" : v >= 30 ? "#f59e0b" : "#ef4444" }}>{v}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {country.gaps.length > 0 && (
            <div>
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Gap Analysis</h4>
              <div className="space-y-2">
                {country.gaps.map((gap) => (
                  <div key={gap.label} className="flex items-start gap-3 px-3 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GAP_COLORS[gap.type] || "#f59e0b" }} />
                    <div>
                      <p className="text-xs font-bold text-white">{gap.label}</p>
                      <p className="text-[10px] text-slate-400 leading-relaxed mt-0.5">{gap.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main Component with Progressive Loading ── */
export const PolicyReadiness = () => {
  // Phase 1: lightweight summary (hero + charts + key findings) — direct fetch
  const [summary, setSummary] = useState(null);
  // Phase 2: full data (all countries, all corporate) — from shared prefetch store
  const [fullData] = usePrefetchOrFetch(
    "policyReadiness",
    `${API}/policy/readiness`,
    {
      validate: (d) => d && Array.isArray(d.countries) && d.countries.length > 0,
      delay: 500,
    }
  );

  const [expandedIdx, setExpandedIdx] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [gapFilter, setGapFilter] = useState("");

  useEffect(() => {
    // Phase 1: fast summary (kept as direct fetch — much smaller payload than the full bootstrap entry)
    axios.get(`${API}/policy/readiness/summary`)
      .then(res => setSummary(res.data))
      .catch(() => {});
  }, []);

  const govCountries = fullData?.countries?.filter(c => c.region !== "Corporate") || [];
  const corpEntities = fullData?.countries?.filter(c => c.region === "Corporate") || [];

  const filtered = (regionFilter === "Corporate" ? corpEntities : govCountries).filter(c => {
    if (searchQuery && !c.country.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (regionFilter && regionFilter !== "Corporate" && c.region !== regionFilter) return false;
    if (gapFilter && !c.gaps.some(g => g.type === gapFilter)) return false;
    return true;
  });

  const regionBarData = (summary?.regions || fullData?.regions || []).map(r => ({
    name: r.region === "Middle East & Africa" ? "MEA" : r.region === "International" ? "Intl" : r.region,
    pri: r.avg_pri, countries: r.countries,
  }));

  // Key finding numbers from summary (fast) or full data (fallback)
  const s = summary || {};

  return (
    <div className="space-y-8 pb-8" data-testid="policy-readiness">
      {/* ─── Phase 1: Hero (shows immediately with summary) ─── */}
      {!summary ? <HeroSkeleton /> : (
        <div className="relative rounded-2xl overflow-hidden animate-fade-in" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #150a1a 50%, #0a0f1a 100%)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(139,92,246,0.08), transparent 60%)" }} />
          <div className="relative px-8 py-10 text-center">
            <p className="text-[10px] font-bold text-violet-400 uppercase tracking-[0.3em] mb-2">From Paper to Impact</p>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-3">Policy Readiness Index</h1>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Measuring whether AI policies translate into real-world impact across{" "}
              <span className="text-violet-400 font-bold">{s.gov_count || s.total_countries}</span> countries
              {s.corp_count > 0 && <> and <span className="text-cyan-400 font-bold">{s.corp_count}</span> tech companies</>}.
              <span className="text-slate-500 ml-1">Not just "do they have policies?" but "are their policies empowering people and enabling ethical AI?"</span>
            </p>
            <div className="mt-6 inline-flex items-center gap-3 px-6 py-3 bg-violet-500/10 border border-violet-500/20 rounded-full">
              <span className="text-[9px] font-bold text-violet-400/60 uppercase tracking-widest">Global PRI</span>
              <span className="text-3xl font-black text-violet-400">{s.global_avg_pri}</span>
              <span className="text-xs text-slate-400">/ 100</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Phase 1: Charts + Key Findings (shows with summary) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!summary ? <ChartSkeleton /> : (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 animate-fade-in" data-testid="regional-pri-chart">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Regional Readiness</h3>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={regionBarData}>
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="pri" name="Avg PRI" radius={[4, 4, 0, 0]}>
                    {regionBarData.map((d) => <Cell key={d.region || d.name} fill={priColor(d.pri)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!summary ? <InsightSkeleton /> : (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 animate-fade-in" data-testid="pri-insights">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Key Findings</h3>
            <div className="space-y-3">
              {s.top_5?.[0] && (
                <div className="flex items-start gap-3 px-3 py-2 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
                  <Shield className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-emerald-400">Most Ready</p>
                    <p className="text-[10px] text-slate-400">{s.top_5[0].country} leads with PRI {s.top_5[0].pri_score}, scoring highest on impact translation and ethical depth.</p>
                  </div>
                </div>
              )}
              {s.ecosystem_gap_count > 0 && (
                <div className="flex items-start gap-3 px-3 py-2 bg-amber-500/5 border border-amber-500/10 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-400">Enabling Potential</p>
                    <p className="text-[10px] text-slate-400">
                      {s.ecosystem_gap_count} countries show high care intent from builders but lack policy infrastructure to scale.
                      The talent and ethics exist; the scaffold doesn't.
                    </p>
                  </div>
                </div>
              )}
              {s.critical_gap_count > 0 && (
                <div className="flex items-start gap-3 px-3 py-2 bg-red-500/5 border border-red-500/10 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-red-400">Critical Gaps</p>
                    <p className="text-[10px] text-slate-400">
                      {s.critical_gap_count} countries have 3+ structural gaps in their AI governance framework,
                      including enforcement, convergence, and equity blind spots.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
                <Globe className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold text-blue-400">Convergence</p>
                  <p className="text-[10px] text-slate-400">
                    {s.convergence_strong || 0} countries show strong global convergence readiness.{" "}
                    {s.convergence_siloed || 0} are domestically siloed with no cross-border cooperation signals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Phase 2: Corporate (shows when full data arrives) ─── */}
      {!fullData ? <CorpSkeleton /> : corpEntities.length > 0 && !regionFilter && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden animate-fade-in" data-testid="corporate-policies">
          <div className="px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-bold text-white">Corporate AI Ethics Policies</h3>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">How tech companies self-regulate compared to government frameworks. {corpEntities.length} companies with formal AI policies.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 p-4">
            {corpEntities.sort((a, b) => b.pri_score - a.pri_score).map((c) => (
              <div key={c.country} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3 hover:border-cyan-500/20 transition-all"
                data-testid={`corp-policy-${c.country.toLowerCase()}`}>
                <p className="text-xs font-bold text-white truncate">{c.country}</p>
                <p className="text-lg font-black mt-1" style={{ color: priColor(c.pri_score) }}>{c.pri_score}</p>
                <p className="text-[9px] text-slate-500">{c.policy_count} policies</p>
                <div className="flex gap-1 mt-2">
                  {Object.entries(c.dimensions).slice(0, 3).map(([k, v]) => (
                    <div key={k} className="h-1 flex-1 rounded-full bg-white/[0.04] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 60 ? "#10b981" : "#f59e0b" }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Phase 2: Full ranking (shows when full data arrives) ─── */}
      {!fullData ? <RankingSkeleton /> : (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden animate-fade-in" data-testid="readiness-ranking">
          <div className="px-5 py-4 border-b border-white/[0.06] flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white">Country Readiness Ranking</h3>
              <p className="text-[10px] text-slate-500">Policy frameworks ranked by real-world impact potential</p>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search countries..."
                className="pl-8 pr-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/30 w-44"
                data-testid="readiness-search" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)}
                className="text-[10px] px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded-lg text-slate-300 focus:outline-none" data-testid="region-filter">
                <option value="">All Regions</option>
                {(fullData?.regions || []).map(r => <option key={r.region} value={r.region}>{r.region}</option>)}
              </select>
              <select value={gapFilter} onChange={e => setGapFilter(e.target.value)}
                className="text-[10px] px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded-lg text-slate-300 focus:outline-none" data-testid="gap-filter">
                <option value="">All Gap Types</option>
                <option value="policy_product">Policy-Product Gap</option>
                <option value="enforcement">Enforcement Gap</option>
                <option value="dimension">Ethical Blind Spots</option>
                <option value="people">People Gap</option>
                <option value="convergence">Isolation Risk</option>
                <option value="ecosystem">Enabling Potential</option>
              </select>
            </div>
          </div>
          <div>
            {filtered.length === 0 && <p className="text-center text-sm text-slate-500 py-8">No countries match your filters</p>}
            {filtered.map((c, i) => (
              <CountryRow key={c.country} country={c} rank={i + 1}
                expanded={expandedIdx === i} onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyReadiness;
